import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import useAdminContext from "../../store/contexts/AdminContext";
import RoomList from "./RoomList";
import RoomDetailModal from "./RoomDetailModal";
import RoomEditModal from "./RoomEditModal";
import RoomStatusDialog from "./RoomStatusDialog";
import { ADMIN_ROLES } from "../../constants/types";

const RoomManagement = () => {
  const {
    admin,
    authState: { loading, error },
    getRoomsListApiCall,
    getRoomByIdApiCall,
    updateRoomApiCall,
    toggleRoomStatusApiCall,
    clearErrors,
    showSnackbar,
  } = useAdminContext();

  // ============= RBAC HELPER =============
  const isSuperAdmin = () => admin?.role === ADMIN_ROLES.SUPER_ADMIN;

  // State for rooms and pagination
  const [rooms, setRooms] = useState([]);
  const [roomPagination, setRoomPagination] = useState({
    total: 0,
    pageNo: 1,
    pageSize: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    room_type: "all",
    is_active: "all",
    pageNo: 1,
    pageSize: 20,
  });

  // Modal states
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomDetailModalOpen, setRoomDetailModalOpen] = useState(false);
  const [roomEditModalOpen, setRoomEditModalOpen] = useState(false);
  const [roomStatusDialogOpen, setRoomStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null); // 'activate' or 'deactivate'

  // Statistics
  const [statistics, setStatistics] = useState({
    totalRooms: 0,
    activeRooms: 0,
    inactiveRooms: 0,
    publicRooms: 0,
    privateRooms: 0,
  });

  // Load rooms on component mount and filter changes
  useEffect(() => {
    clearErrors();
    loadRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNo, filters.pageSize]);

  const loadRooms = async (customFilters = filters) => {
    try {
      const result = await getRoomsListApiCall(customFilters);
      if (result) {
        setRooms(result.rooms || []);
        setRoomPagination(result.pagination || {});

        // Calculate statistics
        calculateStatistics(result.rooms || []);
      }
    } catch (error) {
      console.error("Error loading rooms:", error);
    }
  };

  const calculateStatistics = (roomsList) => {
    const stats = {
      totalRooms: roomsList.length,
      activeRooms: 0,
      inactiveRooms: 0,
      publicRooms: 0,
      privateRooms: 0,
    };

    roomsList.forEach((room) => {
      if (room.is_active) {
        stats.activeRooms++;
      } else {
        stats.inactiveRooms++;
      }

      if (room.room_type === "public") {
        stats.publicRooms++;
      } else if (room.room_type === "private") {
        stats.privateRooms++;
      }
    });

    setStatistics(stats);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
      pageNo: filterName !== "pageNo" ? 1 : value,
    };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    loadRooms(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: "",
      status: "all",
      room_type: "all",
      is_active: "all",
      pageNo: 1,
      pageSize: 20,
    };
    setFilters(clearedFilters);
    loadRooms(clearedFilters);
  };

  const handleRefresh = () => {
    loadRooms();
  };

  const handlePreviousPage = () => {
    if (roomPagination.hasPrev) {
      handleFilterChange("pageNo", roomPagination.pageNo - 1);
    }
  };

  const handleNextPage = () => {
    if (roomPagination.hasNext) {
      handleFilterChange("pageNo", roomPagination.pageNo + 1);
    }
  };

  const handleViewRoom = async (roomId) => {
    try {
      const room = await getRoomByIdApiCall(roomId);
      if (room) {
        setSelectedRoom(room);
        setRoomDetailModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching room details:", error);
    }
  };

  // ============= RBAC PROTECTED HANDLERS =============

  const handleEditRoom = async (room) => {
    // RBAC: Only super admins can edit rooms
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can edit room settings", "error");
      return;
    }

    setSelectedRoom(room);
    setRoomEditModalOpen(true);
  };

  const handleSaveRoomEdit = async (roomId, updateData) => {
    // RBAC: Double-check super admin status
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can edit rooms", "error");
      return;
    }

    try {
      const updatedRoom = await updateRoomApiCall(roomId, updateData);
      if (updatedRoom) {
        // Update the room in the list
        setRooms((prevRooms) =>
          prevRooms.map((room) =>
            room.id === roomId ? { ...room, ...updatedRoom } : room
          )
        );
        setRoomEditModalOpen(false);
        setSelectedRoom(null);
        showSnackbar("Room updated successfully", "success");
      }
    } catch (error) {
      console.error("Error updating room:", error);
    }
  };

  const handleToggleRoomStatus = (room, action) => {
    // RBAC: Only super admins can activate/deactivate rooms
    if (!isSuperAdmin()) {
      showSnackbar(
        "Only super admins can activate or deactivate rooms",
        "error"
      );
      return;
    }

    setSelectedRoom(room);
    setStatusAction(action);
    setRoomStatusDialogOpen(true);
  };

  const handleConfirmStatusToggle = async (reason) => {
    if (!selectedRoom || !statusAction) return;

    // RBAC: Double-check super admin status
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can perform this action", "error");
      return;
    }

    try {
      const isActivating = statusAction === "activate";
      const result = await toggleRoomStatusApiCall(selectedRoom.id, {
        is_active: isActivating,
        reason: !isActivating ? reason : undefined,
      });

      if (result) {
        // Update the room in the list
        setRooms((prevRooms) =>
          prevRooms.map((room) =>
            room.id === selectedRoom.id ? { ...room, ...result } : room
          )
        );
        setRoomStatusDialogOpen(false);
        setSelectedRoom(null);
        setStatusAction(null);
        showSnackbar(
          `Room ${isActivating ? "activated" : "deactivated"} successfully`,
          "success"
        );
        loadRooms(); // Refresh statistics
      }
    } catch (error) {
      console.error("Error toggling room status:", error);
    }
  };

  const handleCloseModals = () => {
    setRoomDetailModalOpen(false);
    setRoomEditModalOpen(false);
    setRoomStatusDialogOpen(false);
    setSelectedRoom(null);
    setStatusAction(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" color="text.primary" gutterBottom>
            Room Management
          </Typography>
          <Typography variant="body1" color="text.primary">
            {isSuperAdmin()
              ? "Manage chat rooms, monitor activity, and moderate content."
              : "View chat rooms and monitor activity (Limited edit access)."}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* RBAC Warning for Regular Admins */}
      {!isSuperAdmin() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have limited access to room management. Only super admins can
          activate, deactivate, or edit room settings. You can view room details
          and monitor activity.
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="primary" gutterBottom>
              {statistics.totalRooms}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Rooms
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="success.main" gutterBottom>
              {statistics.activeRooms}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Rooms
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="error.main" gutterBottom>
              {statistics.inactiveRooms}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inactive Rooms
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="info.main" gutterBottom>
              {statistics.publicRooms}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Public Rooms
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by room name, host..."
              size="small"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="ongoing">Ongoing</MenuItem>
                <MenuItem value="ended">Ended</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="room-type-label">Room Type</InputLabel>
              <Select
                labelId="room-type-label"
                label="Room Type"
                value={filters.room_type}
                onChange={(e) =>
                  handleFilterChange("room_type", e.target.value)
                }
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="active-status-label">Active Status</InputLabel>
              <Select
                labelId="active-status-label"
                label="Active Status"
                value={filters.is_active}
                onChange={(e) =>
                  handleFilterChange("is_active", e.target.value)
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                fullWidth
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Room List */}
      <RoomList
        rooms={rooms}
        loading={loading}
        pagination={roomPagination}
        onViewRoom={handleViewRoom}
        onEditRoom={handleEditRoom}
        onToggleStatus={handleToggleRoomStatus}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        isSuperAdmin={isSuperAdmin()}
      />

      {/* Room Detail Modal */}
      <RoomDetailModal
        open={roomDetailModalOpen}
        room={selectedRoom}
        onClose={handleCloseModals}
        onEdit={handleEditRoom}
        loading={loading}
        isSuperAdmin={isSuperAdmin()}
      />

      {/* Room Edit Modal - Only for Super Admins */}
      {isSuperAdmin() && (
        <RoomEditModal
          open={roomEditModalOpen}
          room={selectedRoom}
          onClose={handleCloseModals}
          onSave={handleSaveRoomEdit}
          loading={loading}
        />
      )}

      {/* Room Status Dialog - Only for Super Admins */}
      {isSuperAdmin() && (
        <RoomStatusDialog
          open={roomStatusDialogOpen}
          room={selectedRoom}
          action={statusAction}
          onClose={handleCloseModals}
          onConfirm={handleConfirmStatusToggle}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default RoomManagement;
